// Saisie de l'état des lieux sur mobile (F-043), avec conservation locale et synchronisation (F-103).
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, del, get, post, put, upload } from "@core/api";
import { dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { useSession } from "@core/session";
import { Badge, Button, Card, ErrorBox, Input, Loading, Modal, Notice, PageHeader, Select, Textarea, fieldError } from "@core/ui";
import { clearInspection, deletePhoto, getDraft, listPhotos, putDraft, putPhoto, useOnline, type QueuedPhoto } from "./offline";
import {
  CONDITIONS,
  DocLink,
  InspectionStatus,
  LEGAL_NOTICE,
  METER_KINDS,
  conditionLabel,
  errMessage,
  newKey,
  useAction,
  type Condition,
  type Inspection,
  type InspectionContent,
  type Item,
  type Photo,
  type Room,
  type SignatureRequest,
} from "./shared";
import { RequestCard } from "./tabs";

type SyncState = "synced" | "pending" | "saving" | "offline" | "conflict" | "error";

const SYNC_LABELS: Record<SyncState, string> = {
  synced: "Enregistré sur le serveur",
  pending: "Modifications à envoyer",
  saving: "Envoi en cours…",
  offline: "Hors connexion : saisie conservée sur cet appareil",
  conflict: "Conflit de version à résoudre",
  error: "Envoi impossible",
};

function QueuedThumb({ blob, label }: { blob: Blob; label: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return (
    <span className="entree-photo">
      {url ? <img src={url} alt={label} /> : null}
      <span className="entree-photo-badge">En attente</span>
    </span>
  );
}

function countPhotos(photos: Photo[], queued: QueuedPhoto[], room: string, item: string | null) {
  return photos.filter((p) => p.room_key === room && p.item_key === item).length + queued.filter((p) => p.roomKey === room && p.itemKey === item).length;
}

export function InspectionPage() {
  const org = useOrg();
  const nav = useNavigate();
  const { user } = useSession();
  const invalidate = useInvalidate();
  const { leaseId = "", inspectionId = "" } = useParams();
  const online = useOnline();
  const path = org.api(`/inspections/${inspectionId}`);
  const q = useGet<Inspection>(path);
  const [content, setContent] = useState<InspectionContent | null>(null);
  const [baseRev, setBaseRev] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [sync, setSync] = useState<SyncState>("synced");
  const [syncError, setSyncError] = useState<string | null>(null);
  const [localOk, setLocalOk] = useState(true);
  const [queued, setQueued] = useState<QueuedPhoto[]>([]);
  const [conflict, setConflict] = useState<Inspection | null>(null);
  const [ready, setReady] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const editSeq = useRef(0);
  const latest = useRef<InspectionContent | null>(null);
  const { busy, error, run } = useAction();
  const server = q.data;
  const editable = !!server && server.status === "brouillon" && org.can("operate");

  // Initialisation : copie locale non envoyée, sinon version du serveur (conservée pour un usage hors connexion).
  useEffect(() => {
    if (ready) return;
    if (!server && !q.error) return;
    let cancelled = false;
    (async () => {
      const local = await getDraft(inspectionId);
      const photos = await listPhotos(inspectionId);
      if (cancelled) return;
      setQueued(photos);
      if (server) {
        if (server.status !== "brouillon") {
          await clearInspection(inspectionId);
          setQueued([]);
          setContent(server.content);
          setBaseRev(server.revision);
        } else if (local && local.dirty) {
          setContent(local.content);
          setBaseRev(local.baseRevision);
          setDirty(true);
          if (local.baseRevision === server.revision) {
            setSync("pending");
          } else {
            setConflict(server);
            setSync("conflict");
          }
        } else {
          setContent(server.content);
          setBaseRev(server.revision);
          const ok = await putDraft({ id: inspectionId, orgId: org.orgId, leaseId, content: server.content, baseRevision: server.revision, dirty: false, savedAt: new Date().toISOString() });
          setLocalOk(ok);
        }
      } else if (local) {
        setContent(local.content);
        setBaseRev(local.baseRevision);
        setDirty(local.dirty);
        setSync("offline");
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [server, q.error, ready, inspectionId, leaseId, org.orgId]);

  const update = (next: InspectionContent) => {
    editSeq.current += 1;
    latest.current = next;
    setContent(next);
    setDirty(true);
    if (sync !== "conflict") setSync(online ? "pending" : "offline");
    putDraft({ id: inspectionId, orgId: org.orgId, leaseId, content: next, baseRevision: baseRev, dirty: true, savedAt: new Date().toISOString() }).then(setLocalOk);
  };

  const flushPhotos = useCallback(
    async (list: QueuedPhoto[]) => {
      for (const p of list) {
        const fd = new FormData();
        fd.append("room_key", p.roomKey);
        if (p.itemKey) fd.append("item_key", p.itemKey);
        fd.append("client_key", p.clientKey);
        fd.append("caption", p.caption);
        fd.append("file", new File([p.blob], p.filename, { type: p.mime }));
        try {
          await upload(`${path}/photos`, fd);
          await deletePhoto(p.clientKey);
          setQueued((xs) => xs.filter((x) => x.clientKey !== p.clientKey));
        } catch (e) {
          if (e instanceof ApiError && e.status === 0) {
            setSync("offline");
            return;
          }
          setSyncError(`Photo non envoyée : ${errMessage(e)}`);
        }
      }
      invalidate(path);
    },
    [path, invalidate],
  );

  const doSync = useCallback(async () => {
    if (!content) return;
    const seq = editSeq.current;
    setSync("saving");
    setSyncError(null);
    try {
      const r = await put<Inspection>(path, { base_revision: baseRev, content });
      setBaseRev(r.revision);
      if (editSeq.current === seq) {
        setDirty(false);
        setSync("synced");
        await putDraft({ id: inspectionId, orgId: org.orgId, leaseId, content, baseRevision: r.revision, dirty: false, savedAt: new Date().toISOString() });
      } else {
        // Saisies faites pendant l'envoi : elles restent à envoyer, fondées sur la nouvelle révision.
        setSync("pending");
        if (latest.current) await putDraft({ id: inspectionId, orgId: org.orgId, leaseId, content: latest.current, baseRevision: r.revision, dirty: true, savedAt: new Date().toISOString() });
      }
      invalidate(path);
      if (queued.length) await flushPhotos(queued);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.message.startsWith("Conflit de version")) {
        try {
          setConflict(await get<Inspection>(path));
        } catch {
          /* le détail du serveur reste indisponible : le conflit est tout de même signalé */
        }
        setSync("conflict");
      } else if (e instanceof ApiError && e.status === 0) {
        setSync("offline");
      } else {
        setSync("error");
        setSyncError(errMessage(e));
      }
    }
  }, [content, baseRev, path, inspectionId, org.orgId, leaseId, invalidate, queued, flushPhotos]);

  // Envoi automatique quelques instants après la dernière saisie, dès que le réseau est là.
  useEffect(() => {
    if (!ready || !editable || !dirty || !online || sync === "conflict" || sync === "saving" || sync === "error") return;
    const t = setTimeout(doSync, 1200);
    return () => clearTimeout(t);
  }, [ready, editable, dirty, online, sync, doSync]);

  useEffect(() => {
    if (online && sync === "offline") setSync(dirty ? "pending" : "synced");
    if (!online && (sync === "pending" || sync === "synced") && dirty) setSync("offline");
  }, [online, sync, dirty]);

  // Photos en attente envoyées dès que la saisie est à jour sur le serveur.
  useEffect(() => {
    if (ready && online && !dirty && queued.length && sync === "synced") flushPhotos(queued);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, online, dirty, sync]);

  const addPhotos = async (files: File[], room: string, item: string | null, caption: string) => {
    for (const f of files) {
      const p: QueuedPhoto = { clientKey: newKey(), inspectionId, orgId: org.orgId, roomKey: room, itemKey: item, caption, filename: f.name || "photo.jpg", mime: f.type || "image/jpeg", blob: f, createdAt: new Date().toISOString() };
      const stored = await putPhoto(p);
      if (!stored) {
        setLocalOk(false);
        if (!online) {
          setSyncError("Photo non conservée : le stockage de cet appareil est indisponible et le réseau absent.");
          continue;
        }
      }
      setQueued((xs) => [...xs, p]);
      if (online && !dirty && sync !== "conflict") await flushPhotos([p]);
    }
  };

  if (q.isLoading && !ready) return <Loading label="Chargement de l’état des lieux…" />;
  if (!ready || !content) {
    return (
      <div className="page">
        <ErrorBox error={q.error ?? new Error("État des lieux indisponible.")} />
        {!online ? <Notice tone="attention">Hors connexion et aucune copie sur cet appareil : ouvrez cet état des lieux une fois avec du réseau pour pouvoir le saisir ensuite hors connexion.</Notice> : null}
        <Link to={org.to(`baux/${leaseId}?onglet=edl`)}>Revenir au bail</Link>
      </div>
    );
  }

  const s = server;
  const photos = s?.photos ?? [];
  const lease = s?.lease;
  const setRoom = (i: number, patch: Partial<Room>) => update({ ...content, rooms: content.rooms.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const setItem = (ri: number, ii: number, patch: Partial<Item>) => setRoom(ri, { items: content.rooms[ri].items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) });
  const missing = content.rooms.reduce((n, r) => n + r.items.filter((i) => !i.condition).length, 0);
  const canValidate = editable && online && !dirty && sync === "synced" && queued.length === 0;

  return (
    <div className="page entree-edl">
      <PageHeader
        eyebrow={lease ? `${lease.property_label} · version ${s?.version}` : `Version ${s?.version ?? ""}`}
        title={s?.kind_label ?? "État des lieux"}
        intro={s ? <InspectionStatus i={s} /> : null}
        actions={<Link to={org.to(`baux/${leaseId}?onglet=edl`)}>Revenir au bail</Link>}
      />
      {editable || dirty ? (
        <div className="entree-syncbar" role="status" aria-live="polite">
          <span>
            <span className="entree-sync-dot" data-state={sync} aria-hidden="true" />
            {SYNC_LABELS[sync]}
            {queued.length ? ` · ${queued.length} photo(s) en attente d’envoi` : ""}
          </span>
          <span className="entree-actions">
            {!online ? <Badge tone="attention">Hors connexion</Badge> : null}
            {online && dirty && sync !== "conflict" ? (
              <Button small variant="secondaire" busy={sync === "saving"} onClick={doSync}>
                Envoyer maintenant
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}
      {!localOk ? <Notice tone="attention">Le stockage de cet appareil est indisponible (navigation privée ou stockage bloqué) : sans réseau, la saisie ne sera pas conservée.</Notice> : null}
      {syncError ? <Notice tone="erreur">{syncError}</Notice> : null}
      {!online && editable ? <Notice>Vos saisies et photos restent sur cet appareil jusqu’au retour du réseau. La validation et la signature nécessitent une connexion.</Notice> : null}
      {sync === "conflict" ? (
        <Card title="Conflit de version">
          <p>
            L’état des lieux a été modifié ailleurs depuis votre dernière synchronisation
            {conflict ? ` (le ${dateTime(conflict.updated_at)}, ${conflict.stats.rooms} pièce(s), ${conflict.stats.items} élément(s) sur le serveur ; ${content.rooms.length} pièce(s) sur cet appareil)` : ""}. Rien n’est écrasé sans votre choix.
          </p>
          <div className="entree-actions">
            <Button
              onClick={() => {
                if (!conflict) return;
                setBaseRev(conflict.revision);
                setConflict(null);
                setSync("pending");
              }}
              disabled={!conflict}
            >
              Garder la version de cet appareil
            </Button>
            <Button
              variant="secondaire"
              onClick={async () => {
                if (!conflict) return;
                setContent(conflict.content);
                setBaseRev(conflict.revision);
                setDirty(false);
                setConflict(null);
                setSync("synced");
                await putDraft({ id: inspectionId, orgId: org.orgId, leaseId, content: conflict.content, baseRevision: conflict.revision, dirty: false, savedAt: new Date().toISOString() });
              }}
              disabled={!conflict}
            >
              Reprendre la version du serveur
            </Button>
          </div>
        </Card>
      ) : null}

      {s && s.status !== "brouillon" ? <SignedSummary s={s} leaseId={leaseId} /> : null}

      {content.rooms.map((room, ri) => (
        <details key={room.key} className="entree-room" open={editable && ri === 0}>
          <summary>
            <span>{room.name}</span>
            <span className="small muted">
              {room.items.filter((i) => i.condition).length}/{room.items.length} renseigné(s)
            </span>
          </summary>
          <div className="entree-room-body">
            {editable ? <Input label="Nom de la pièce" value={room.name} onChange={(e) => setRoom(ri, { name: e.target.value })} /> : null}
            {room.items.map((it, ii) => {
              const n = countPhotos(photos, queued, room.key, it.key);
              return (
                <div key={it.key} className="entree-item">
                  <div className="entree-item-head">
                    {editable ? (
                      <input className="entree-inline-input" aria-label="Nom de l’élément" value={it.name} onChange={(e) => setItem(ri, ii, { name: e.target.value })} />
                    ) : (
                      <span className="entree-item-name">{it.name}</span>
                    )}
                    {!editable ? <span>{conditionLabel(it.condition)}</span> : null}
                  </div>
                  {editable ? (
                    <fieldset className="entree-conditions">
                      <legend>État de {it.name}</legend>
                      {CONDITIONS.map((c) => (
                        <label key={c.value}>
                          <input type="radio" name={`c-${it.key}`} value={c.value} checked={it.condition === c.value} onChange={() => setItem(ri, ii, { condition: c.value as Condition })} />
                          <span>{c.short}</span>
                        </label>
                      ))}
                    </fieldset>
                  ) : null}
                  {editable ? (
                    <textarea className="entree-textarea" aria-label={`Observations sur ${it.name}`} placeholder="Observations (rayure, tache, fonctionnement…)" rows={2} value={it.comment} onChange={(e) => setItem(ri, ii, { comment: e.target.value })} />
                  ) : it.comment ? (
                    <span className="small">{it.comment}</span>
                  ) : null}
                  <div className="entree-photos">
                    {photos
                      .filter((p) => p.room_key === room.key && p.item_key === it.key)
                      .map((p) => (
                        <span key={p.id} className="entree-photo">
                          <a href={p.url} target="_blank" rel="noreferrer">
                            <img src={p.url} alt={`Photo : ${room.name}, ${it.name}`} loading="lazy" />
                          </a>
                          {editable && online ? (
                            <button
                              type="button"
                              className="entree-photo-badge"
                              aria-label="Supprimer la photo"
                              onClick={() => run(`rm-${p.id}`, async () => { await del(`${path}/photos/${p.id}`); invalidate(path); }, "Photo supprimée.")}
                            >
                              Supprimer
                            </button>
                          ) : null}
                        </span>
                      ))}
                    {queued
                      .filter((p) => p.roomKey === room.key && p.itemKey === it.key)
                      .map((p) => (
                        <QueuedThumb key={p.clientKey} blob={p.blob} label={`Photo en attente : ${it.name}`} />
                      ))}
                    {editable ? (
                      <label className="entree-photo-add">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          className="sr-only"
                          onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            e.target.value = "";
                            if (files.length) addPhotos(files, room.key, it.key, it.comment);
                          }}
                        />
                        Ajouter une photo
                      </label>
                    ) : null}
                    {n === 0 ? <span className="small muted">Aucune photo</span> : null}
                  </div>
                  {editable ? (
                    <div>
                      <Button
                        variant="discret"
                        small
                        onClick={() => {
                          if (n > 0) {
                            setSyncError("Supprimez d’abord les photos de cet élément.");
                            return;
                          }
                          setRoom(ri, { items: room.items.filter((_, j) => j !== ii) });
                        }}
                      >
                        Retirer l’élément
                      </Button>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {editable ? <AddLine label="Ajouter un élément" onAdd={(name) => setRoom(ri, { items: [...room.items, { key: newKey(), name, condition: null, comment: "" }] })} /> : null}
            {editable ? (
              <textarea className="entree-textarea" aria-label={`Commentaire sur la pièce ${room.name}`} placeholder="Commentaire sur la pièce" rows={2} value={room.comment} onChange={(e) => setRoom(ri, { comment: e.target.value })} />
            ) : room.comment ? (
              <p className="small">{room.comment}</p>
            ) : null}
            {editable ? (
              <div>
                <Button
                  variant="discret"
                  small
                  onClick={() => {
                    if (photos.some((p) => p.room_key === room.key) || queued.some((p) => p.roomKey === room.key)) {
                      setSyncError("Supprimez d’abord les photos de cette pièce.");
                      return;
                    }
                    update({ ...content, rooms: content.rooms.filter((_, j) => j !== ri) });
                  }}
                >
                  Retirer la pièce
                </Button>
              </div>
            ) : null}
          </div>
        </details>
      ))}
      {editable ? (
        <Card>
          <AddLine label="Ajouter une pièce" onAdd={(name) => update({ ...content, rooms: [...content.rooms, { key: newKey(), name, comment: "", items: [] }] })} />
        </Card>
      ) : null}

      <Card title="Compteurs">
        {content.meters.map((m, i) => (
          <div key={m.key} className="entree-grid-form">
            {editable ? (
              <>
                <Select label="Type" value={m.kind} options={METER_KINDS} onChange={(e) => update({ ...content, meters: content.meters.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)) })} />
                <Input label="Libellé" value={m.label} onChange={(e) => update({ ...content, meters: content.meters.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <Input label="Numéro" value={m.number} onChange={(e) => update({ ...content, meters: content.meters.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)) })} />
                <Input label={`Index${m.unit ? ` (${m.unit})` : ""}`} inputMode="decimal" value={m.reading} onChange={(e) => update({ ...content, meters: content.meters.map((x, j) => (j === i ? { ...x, reading: e.target.value } : x)) })} />
                <Button variant="discret" small onClick={() => update({ ...content, meters: content.meters.filter((_, j) => j !== i) })}>
                  Retirer
                </Button>
              </>
            ) : (
              <span>
                {m.label || METER_KINDS.find((k) => k.value === m.kind)?.label} {m.number ? `n° ${m.number}` : ""} : <strong>{m.reading || "non relevé"}</strong> {m.unit}
              </span>
            )}
          </div>
        ))}
        {editable ? (
          <Button variant="secondaire" small onClick={() => update({ ...content, meters: [...content.meters, { key: newKey(), kind: "autre", label: "", number: "", reading: "", unit: "" }] })}>
            Ajouter un compteur
          </Button>
        ) : null}
      </Card>

      <Card title="Clés et badges">
        {content.keys.map((k, i) => (
          <div key={k.key} className="entree-grid-form">
            {editable ? (
              <>
                <Input label="Désignation" value={k.label} onChange={(e) => update({ ...content, keys: content.keys.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <Input label="Nombre" type="number" min={0} value={String(k.count)} onChange={(e) => update({ ...content, keys: content.keys.map((x, j) => (j === i ? { ...x, count: Number(e.target.value) || 0 } : x)) })} />
                <Button variant="discret" small onClick={() => update({ ...content, keys: content.keys.filter((_, j) => j !== i) })}>
                  Retirer
                </Button>
              </>
            ) : (
              <span>
                {k.label} : <strong>{k.count}</strong>
              </span>
            )}
          </div>
        ))}
        {editable ? (
          <Button variant="secondaire" small onClick={() => update({ ...content, keys: [...content.keys, { key: newKey(), label: "", count: 1 }] })}>
            Ajouter une ligne
          </Button>
        ) : null}
      </Card>

      <Card title="Personnes présentes et observations">
        {content.participants.map((p, i) => (
          <div key={i} className="entree-grid-form">
            {editable ? (
              <>
                <Input label="Nom" value={p.name} onChange={(e) => update({ ...content, participants: content.participants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                <Input label="Qualité" value={p.quality} onChange={(e) => update({ ...content, participants: content.participants.map((x, j) => (j === i ? { ...x, quality: e.target.value } : x)) })} />
                <Button variant="discret" small onClick={() => update({ ...content, participants: content.participants.filter((_, j) => j !== i) })}>
                  Retirer
                </Button>
              </>
            ) : (
              <span>
                {p.name} {p.quality ? `(${p.quality})` : ""}
              </span>
            )}
          </div>
        ))}
        {editable ? (
          <>
            <Button variant="secondaire" small onClick={() => update({ ...content, participants: [...content.participants, { name: "", quality: "" }] })}>
              Ajouter une personne présente
            </Button>
            <Textarea label="Observations générales" value={content.general_comment} onChange={(e) => update({ ...content, general_comment: e.target.value })} />
          </>
        ) : content.general_comment ? (
          <p>{content.general_comment}</p>
        ) : null}
      </Card>

      {editable ? (
        <Card title="Validation">
          <p className="small muted">{LEGAL_NOTICE}</p>
          {missing ? <Notice tone="attention">{missing} élément(s) sans état : complétez-les ou retirez-les avant la validation.</Notice> : null}
          {!canValidate ? <p className="small">La validation nécessite une connexion et une saisie entièrement envoyée.</p> : null}
          <ErrorBox error={error} />
          <div className="entree-actions">
            <Button disabled={!canValidate || missing > 0} onClick={() => setSignOpen(true)}>
              Valider l’état des lieux
            </Button>
            <Button
              variant="secondaire"
              disabled={!canValidate || missing > 0}
              busy={busy === "esign"}
              onClick={() => run("esign", async () => { await post(`${path}/signature`, {}); invalidate(path, org.api("/leases")); }, "Demande de signature électronique envoyée.")}
            >
              Faire signer électroniquement
            </Button>
          </div>
        </Card>
      ) : null}

      {s?.signature_request_id ? <InspectionSignature requestId={s.signature_request_id} /> : null}

      <Card title="Données de cet appareil">
        <p className="small muted">Une copie de cet état des lieux et les photos en attente sont conservées dans ce navigateur pour permettre la saisie sans réseau. Effacez-les sur un appareil partagé ou perdu.</p>
        <Button
          variant="discret"
          small
          disabled={dirty || queued.length > 0}
          onClick={() => run("clear", async () => { await clearInspection(inspectionId); }, "Copie locale effacée.")}
        >
          Effacer la copie locale
        </Button>
        {dirty || queued.length > 0 ? <span className="small muted"> Des saisies ne sont pas encore envoyées : envoyez-les avant d’effacer.</span> : null}
      </Card>

      {s && lease ? (
        <SignModal
          open={signOpen}
          onClose={() => setSignOpen(false)}
          path={path}
          defaultName={user?.full_name ?? ""}
          tenants={lease.parties.filter((p) => p.role === "locataire")}
          onDone={async () => {
            await clearInspection(inspectionId);
            setQueued([]);
            setSignOpen(false);
            await invalidate(path, org.api("/leases"));
            setReady(false);
          }}
        />
      ) : null}
      {s && s.status === "signe" && org.can("operate") && !s.superseded_by ? (
        <Card title="Corriger">
          <p className="small">Une correction crée une nouvelle version à valider ; la version validée reste intacte.</p>
          <Button
            variant="secondaire"
            busy={busy === "correct"}
            onClick={async () => {
              let id = "";
              if (await run("correct", async () => { const r = await post<Inspection>(`${path}/correct`); id = r.id; invalidate(org.api("/leases")); }, "Nouvelle version créée.")) nav(org.to(`baux/${leaseId}/etats-des-lieux/${id}`));
            }}
          >
            Corriger (nouvelle version)
          </Button>
        </Card>
      ) : null}
    </div>
  );
}

function AddLine({ label, onAdd }: { label: string; onAdd: (name: string) => void }) {
  const [name, setName] = useState("");
  return (
    <form
      className="entree-grid-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onAdd(name.trim());
        setName("");
      }}
    >
      <Input label={label} value={name} onChange={(e) => setName(e.target.value)} />
      <Button type="submit" variant="secondaire" small disabled={!name.trim()}>
        Ajouter
      </Button>
    </form>
  );
}

function SignedSummary({ s, leaseId }: { s: Inspection; leaseId: string }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const { busy, error, run } = useAction();
  const path = org.api(`/inspections/${s.id}`);
  return (
    <Card title={s.status === "signe" ? "Validé par les parties" : "En attente de validation"}>
      <ErrorBox error={error} />
      <ul className="list-plain">
        {s.landlord_signed_at ? (
          <li>
            Bailleur : {s.landlord_signer_name}, le {dateTime(s.landlord_signed_at)}
          </li>
        ) : null}
        {s.tenant_signed_at ? (
          <li>
            Locataire : {s.tenant_signer_name}, le {dateTime(s.tenant_signed_at)}
            {s.tenant_signature_mode === "compte" ? " (depuis son espace)" : s.tenant_signature_mode === "sur_place" ? " (sur place)" : s.tenant_signature_mode === "prestataire" ? " (signature électronique)" : ""}
          </li>
        ) : null}
        {s.tenant_absence_note ? <li>Locataire absent : {s.tenant_absence_note}</li> : null}
      </ul>
      {s.reserves ? <Notice title="Réserves">{s.reserves}</Notice> : null}
      {s.content_sha256 ? <p className="small muted">Empreinte du contenu validé : {s.content_sha256.slice(0, 16)}…</p> : null}
      {s.pdf ? <DocLink doc={s.pdf} label="État des lieux (PDF)" /> : <a href={`${path}/preview`} target="_blank" rel="noreferrer">Aperçu PDF</a>}
      {s.status === "en_signature" && !s.signature_request_id && org.can("operate") ? (
        <div>
          <p className="small">Le locataire valide depuis son espace. Pour modifier le contenu, rouvrez la saisie (la validation du bailleur est alors retirée).</p>
          <Button variant="secondaire" small busy={busy === "reopen"} onClick={() => run("reopen", async () => { await post(`${path}/reopen`); invalidate(path, org.api("/leases")); window.location.assign(org.to(`baux/${leaseId}/etats-des-lieux/${s.id}`)); }, "Saisie rouverte.")}>
            Rouvrir la saisie
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function InspectionSignature({ requestId }: { requestId: string }) {
  const org = useOrg();
  const q = useGet<SignatureRequest>(org.api(`/signature-requests/${requestId}`));
  const sys = useGet<{ provider: { simulateur: boolean } }>(q.data ? org.api(`/leases/${q.data.lease_id}/signature`) : null);
  if (!q.data) return q.isLoading ? <Loading /> : <ErrorBox error={q.error} />;
  return <RequestCard r={q.data} simulator={sys.data?.provider.simulateur ?? q.data.mode === "simulateur"} />;
}

function SignModal({ open, onClose, path, defaultName, tenants, onDone }: { open: boolean; onClose: () => void; path: string; defaultName: string; tenants: { full_name: string; account_linked: boolean }[]; onDone: () => Promise<void> }) {
  const [f, setF] = useState({ landlord_name: defaultName, tenant_mode: "sur_place", tenant_name: tenants.map((t) => t.full_name).join(", "), absence_note: "", reserves: "", performed_at: "" });
  const { busy, error, run } = useAction();
  const linked = tenants.some((t) => t.account_linked);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Valider l’état des lieux"
      footer={
        <Button
          busy={busy === "sign"}
          onClick={async () => {
            const ok = await run(
              "sign",
              () =>
                post(`${path}/sign`, {
                  landlord_name: f.landlord_name,
                  tenant_mode: f.tenant_mode,
                  tenant_name: f.tenant_mode === "sur_place" ? f.tenant_name : null,
                  absence_note: f.tenant_mode === "absent" ? f.absence_note : null,
                  reserves: f.reserves,
                  performed_at: f.performed_at ? new Date(f.performed_at).toISOString() : null,
                }),
              "Validation enregistrée et horodatée.",
            );
            if (ok) await onDone();
          }}
        >
          Valider
        </Button>
      }
    >
      <div className="stack">
        <ErrorBox error={error} />
        <p className="small muted">Chaque validation est horodatée et liée au contenu (empreinte). Le contenu ne peut plus être modifié ensuite, sauf par une nouvelle version.</p>
        <Input label="Validé pour le bailleur par" value={f.landlord_name} error={fieldError(error, "landlord_name")} onChange={(e) => setF({ ...f, landlord_name: e.target.value })} />
        <Input label="Date et heure de l’état des lieux" type="datetime-local" value={f.performed_at} help="Laissez vide pour l’heure actuelle." onChange={(e) => setF({ ...f, performed_at: e.target.value })} />
        <Select
          label="Validation du locataire"
          value={f.tenant_mode}
          options={[
            { value: "sur_place", label: "Le locataire valide sur cet appareil" },
            ...(linked ? [{ value: "compte", label: "Le locataire valide depuis son espace" }] : []),
            { value: "absent", label: "Le locataire est absent" },
          ]}
          onChange={(e) => setF({ ...f, tenant_mode: e.target.value })}
        />
        {f.tenant_mode === "sur_place" ? <Input label="Nom du locataire qui valide" value={f.tenant_name} error={fieldError(error, "tenant_name")} onChange={(e) => setF({ ...f, tenant_name: e.target.value })} /> : null}
        {f.tenant_mode === "absent" ? <Textarea label="Circonstances de l’absence" value={f.absence_note} error={fieldError(error, "absence_note")} onChange={(e) => setF({ ...f, absence_note: e.target.value })} help="Par exemple : constat établi par commissaire de justice." /> : null}
        <Textarea label="Réserves (facultatif)" value={f.reserves} error={fieldError(error, "reserves")} onChange={(e) => setF({ ...f, reserves: e.target.value })} />
      </div>
    </Modal>
  );
}
